import React, { useState, useEffect, useMemo } from 'react';
import { X, ConciergeBell, Check, Loader2, AlertCircle, Search } from 'lucide-react';
// import { userService } from '../../../services/userService';
import userService from '@/services/userService';
export default function WaiterSelectionModal({
  isOpen,
  onClose,
  onSelectWaiter,
  linkedWaiter,
  onUnlinkWaiter
}) {
  const [selectedWaiter, setSelectedWaiter] = useState(null);
  const [waiters, setWaiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load waiters when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedWaiter(null);
      setError(null);
      setSearchQuery(''); // Reset search on open
      fetchWaiters();
    }
  }, [isOpen]);

  // Filter waiters based on search query
  const filteredWaiters = useMemo(() => {
    if (!searchQuery.trim()) return waiters;
    const query = searchQuery.toLowerCase();
    return waiters.filter(waiter =>
      waiter.name.toLowerCase().includes(query) ||
      waiter.email?.toLowerCase().includes(query)
    );
  }, [waiters, searchQuery]);

  // Fetch waiters from API
  const fetchWaiters = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.getWaiters();

      // Handle different response structures
      let waiterData = [];
      if (Array.isArray(response)) 
        waiterData = response;
      else if (Array.isArray(response?.data)) waiterData = response.data;
    else if (response?.data?.data && Array.isArray(response.data.data)) {
      waiterData = response.data.data;
    }

   const mappedWaiters = waiterData.map(user => ({
  id: user.id,
  name: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
        || user.email
        || "Unknown",
  email: user.email,
  role_name: user.roleName,  
  active: user.active !== false,
}));

      // Filter to only active waiters
      setWaiters(
      mappedWaiters.filter(w => w.active && w.role_name?.toLowerCase() === "waiter")
    );
    } catch (err) {
      console.error('Failed to fetch waiters:', err);
      setError('Failed to load waiters. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    if (selectedWaiter) {
      onSelectWaiter(selectedWaiter);
    }
  };

  const handleRetry = () => {
    fetchWaiters();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[70vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-teal-50 to-cyan-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center shadow-sm">
              <ConciergeBell size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Assign Waiter</h2>
              <p className="text-xs text-slate-500">Select server for this order</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-xl transition-all"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Search Input */}
        {!loading && !error && waiters.length > 0 && (
          <div className="px-4 pt-3 pb-2">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search waiters..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                >
                  <X size={14} className="text-slate-400" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Waiter List */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={32} className="text-teal-500 animate-spin mb-3" />
              <p className="text-sm text-slate-500">Loading waiters...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={28} className="text-red-500" />
              </div>
              <p className="text-slate-600 font-semibold mb-1">Failed to load waiters</p>
              <p className="text-sm text-slate-500 mb-4">{error}</p>
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition"
              >
                Retry
              </button>
            </div>
          ) : waiters.length > 0 ? (
            filteredWaiters.length > 0 ? (
              <div className="space-y-2">
                {filteredWaiters.map((waiter) => {
                  const isSelected = selectedWaiter?.id === waiter.id;
                  const isLinked = linkedWaiter?.id === waiter.id;

                  return (
                    <button
                      key={waiter.id}
                      onClick={() => !isLinked && setSelectedWaiter(waiter)}
                      disabled={isLinked}
                      className={`w-full p-3 rounded-xl border-2 transition-all text-left ${
                        isLinked
                          ? 'border-teal-300 bg-teal-50 cursor-default'
                          : isSelected
                          ? 'border-teal-500 bg-teal-50 shadow-md'
                          : 'border-slate-200 hover:border-teal-300 bg-white hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                            isLinked ? 'bg-teal-400' : isSelected ? 'bg-teal-500' : 'bg-slate-400'
                          }`}>
                            {waiter.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-bold text-slate-800">{waiter.name}</span>
                        </div>
                        {isSelected && !isLinked && (
                          <div className="w-7 h-7 bg-teal-500 rounded-full flex items-center justify-center">
                            <Check size={16} className="text-white" />
                          </div>
                        )}
                        {isLinked && (
                          <span className="text-xs bg-teal-200 text-teal-700 px-2 py-1 rounded-full font-semibold">
                            Assigned
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Search size={24} className="text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No waiters match "{searchQuery}"</p>
              </div>
            )
          ) : (
            <div className="text-center py-10">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ConciergeBell size={28} className="text-slate-400" />
              </div>
              <p className="text-slate-600 font-semibold mb-1">No active waiters</p>
              <p className="text-sm text-slate-500">Add waiters with WAITER role in settings</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t-2 border-slate-100 p-4 bg-slate-50">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-white text-slate-600 rounded-xl font-semibold hover:bg-slate-100 transition-all border-2 border-slate-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedWaiter || linkedWaiter?.id === selectedWaiter?.id}
              className="flex-1 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-bold transition-all disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Check size={18} />
              Assign
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
